/**
 * Error codes for consistent error identification across the API.
 * These codes are included in API error responses for easy client-side mapping.
 */
export enum ErrorCode {
  // Authentication errors
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
  ACCOUNT_LOCKED = 'ACCOUNT_LOCKED',
  ACCOUNT_DISABLED = 'ACCOUNT_DISABLED',
  SUBSCRIPTION_CANCELLED = 'SUBSCRIPTION_CANCELLED',
  FREE_PLAN_NON_ADMIN = 'FREE_PLAN_NON_ADMIN',
  PLAN_CHANGE_COOLDOWN = 'PLAN_CHANGE_COOLDOWN',
  USER_NOT_ASSOCIATED_WITH_BUSINESS = 'USER_NOT_ASSOCIATED_WITH_BUSINESS',
  ACCOUNT_NOT_FULLY_ACTIVATED = 'ACCOUNT_NOT_FULLY_ACTIVATED',

  // PIN authentication errors
  PIN_NOT_CONFIGURED = 'PIN_NOT_CONFIGURED',
  MAX_ATTEMPTS_EXCEEDED = 'MAX_ATTEMPTS_EXCEEDED',

  // CAPTCHA errors — Cloudflare Turnstile gate on login (after 3 failed
  // attempts) + always on register / forgot-password.
  CAPTCHA_REQUIRED = 'CAPTCHA_REQUIRED',
  CAPTCHA_INVALID = 'CAPTCHA_INVALID',

  // Registration errors
  EMAIL_ALREADY_REGISTERED = 'EMAIL_ALREADY_REGISTERED',
  /** Tenant-scoped user create: another active user owns this email. */
  USER_EMAIL_ALREADY_EXISTS = 'USER_EMAIL_ALREADY_EXISTS',
  /** Tenant-scoped user create: another active user owns this username. */
  USER_USERNAME_ALREADY_EXISTS = 'USER_USERNAME_ALREADY_EXISTS',
  INVALID_VERIFICATION_TOKEN = 'INVALID_VERIFICATION_TOKEN',
  VERIFICATION_TOKEN_EXPIRED = 'VERIFICATION_TOKEN_EXPIRED',
  ACCOUNT_ALREADY_VERIFIED = 'ACCOUNT_ALREADY_VERIFIED',
  EMAIL_SEND_FAILED = 'EMAIL_SEND_FAILED',

  // Onboarding errors
  INVALID_ONBOARDING_TOKEN = 'INVALID_ONBOARDING_TOKEN',
  ONBOARDING_TOKEN_EXPIRED = 'ONBOARDING_TOKEN_EXPIRED',
  ONBOARDING_ALREADY_COMPLETED = 'ONBOARDING_ALREADY_COMPLETED',
  ONBOARDING_PASSWORD_INVALID = 'ONBOARDING_PASSWORD_INVALID',
  REQUIRES_ONBOARDING = 'REQUIRES_ONBOARDING',

  // Password reset errors
  INVALID_RESET_TOKEN = 'INVALID_RESET_TOKEN',
  RESET_TOKEN_EXPIRED = 'RESET_TOKEN_EXPIRED',
  RESET_TOKEN_ALREADY_USED = 'RESET_TOKEN_ALREADY_USED',

  // Google OAuth errors
  GOOGLE_AUTH_NOT_CONFIGURED = 'GOOGLE_AUTH_NOT_CONFIGURED',
  GOOGLE_INVALID_TOKEN = 'GOOGLE_INVALID_TOKEN',
  ACCOUNT_EXISTS_WITH_GOOGLE = 'ACCOUNT_EXISTS_WITH_GOOGLE',
  ACCOUNT_EXISTS_WITH_EMAIL = 'ACCOUNT_EXISTS_WITH_EMAIL',
  // A completed account already uses this email — sadmin onboarding /
  // account creation (generic, not Google-specific).
  ACCOUNT_EMAIL_ALREADY_EXISTS = 'ACCOUNT_EMAIL_ALREADY_EXISTS',
  GOOGLE_ACCOUNT_NOT_REGISTERED = 'GOOGLE_ACCOUNT_NOT_REGISTERED',

  // Refresh token errors
  REFRESH_TOKEN_EXPIRED = 'REFRESH_TOKEN_EXPIRED',
  REFRESH_TOKEN_REVOKED = 'REFRESH_TOKEN_REVOKED',
  REFRESH_TOKEN_REUSE_DETECTED = 'REFRESH_TOKEN_REUSE_DETECTED',
  SESSION_LIMIT_EXCEEDED = 'SESSION_LIMIT_EXCEEDED',

  // Business scope errors
  BUSINESS_NOT_FOUND = 'BUSINESS_NOT_FOUND',

  // Trial errors
  TRIAL_EXPIRED = 'TRIAL_EXPIRED',

  // Prepaid e-CF capacity packs
  /** Purchases are not enabled yet (v1 gate). */
  PREPAID_PACK_SALES_DISABLED = 'PREPAID_PACK_SALES_DISABLED',
  /**
   * No card or PayPal available. A pack only grants capacity on CONFIRMED
   * payment, so v1 refuses rather than granting on intent the way recurring
   * add-ons do. Manual payment support is a future audited workflow.
   */
  PREPAID_PACK_PAYMENT_METHOD_REQUIRED = 'PREPAID_PACK_PAYMENT_METHOD_REQUIRED',

  // Plan limit errors
  PLAN_LIMIT_INVOICES = 'PLAN_LIMIT_INVOICES',
  PLAN_LIMIT_INVOICES_TOTAL = 'PLAN_LIMIT_INVOICES_TOTAL',
  PLAN_LIMIT_PRODUCTS = 'PLAN_LIMIT_PRODUCTS',
  PLAN_LIMIT_USERS = 'PLAN_LIMIT_USERS',
  PLAN_LIMIT_WAREHOUSES = 'PLAN_LIMIT_WAREHOUSES',
  PLAN_LIMIT_BRANCHES = 'PLAN_LIMIT_BRANCHES',
  PLAN_LIMIT_KDS_NOT_AVAILABLE = 'PLAN_LIMIT_KDS_NOT_AVAILABLE',
  PLAN_LIMIT_BRANCHES_OVERAGE = 'PLAN_LIMIT_BRANCHES_OVERAGE',
  PLAN_LIMIT_USERS_OVERAGE = 'PLAN_LIMIT_USERS_OVERAGE',

  // KDS hierarchy errors (PR 4 / 5)
  KDS_NODE_ACCESS_DENIED = 'KDS_NODE_ACCESS_DENIED',
  KDS_NODE_NOT_FOUND = 'KDS_NODE_NOT_FOUND',
  KDS_NODE_NOT_ENTRY = 'KDS_NODE_NOT_ENTRY',
  KDS_NODE_HAS_LIVE_ORDERS = 'KDS_NODE_HAS_LIVE_ORDERS',
  KDS_NODE_CYCLE_DETECTED = 'KDS_NODE_CYCLE_DETECTED',
  RESTAURANT_ENVIRONMENT_HAS_OPEN_ACCOUNT = 'RESTAURANT_ENVIRONMENT_HAS_OPEN_ACCOUNT',
  RESTAURANT_TABLE_HAS_OPEN_ACCOUNT = 'RESTAURANT_TABLE_HAS_OPEN_ACCOUNT',
  KDS_SCREEN_QUOTA_EXCEEDED = 'KDS_SCREEN_QUOTA_EXCEEDED',
  /** Capacity-modal contract — supersedes KDS_SCREEN_QUOTA_EXCEEDED. */
  KDS_QUOTA_EXCEEDED = 'KDS_QUOTA_EXCEEDED',
  /** user_kds_access.accessLevel = READER — mutations denied. */
  KDS_READER_CANNOT_MUTATE = 'KDS_READER_CANNOT_MUTATE',

  // Inventory errors
  CATEGORY_HAS_CHILDREN = 'CATEGORY_HAS_CHILDREN',
  CATEGORY_HAS_PRODUCTS = 'CATEGORY_HAS_PRODUCTS',
  /** DELETE /inventory/categories/:id — the seeded generic (code='GENERIC')
   *  category is the auto-assigned default for new products and cannot be
   *  deleted. */
  CATEGORY_GENERIC_PROTECTED = 'CATEGORY_GENERIC_PROTECTED',
  /** POST /inventory/reset — confirmation body is missing or not the RESET keyword. */
  INVENTORY_RESET_INVALID_CONFIRMATION = 'INVENTORY_RESET_INVALID_CONFIRMATION',
  /** POST /inventory/reset — branchOfficeId does not belong to the caller's business. */
  INVENTORY_RESET_BRANCH_NOT_FOUND = 'INVENTORY_RESET_BRANCH_NOT_FOUND',
  /** POST /inventory/reset — business has no HQ branch (misconfigured tenant). */
  INVENTORY_RESET_HQ_MISSING = 'INVENTORY_RESET_HQ_MISSING',
  /** POST /business/branch-offices — inventoryMode='empty' with
   *  warehouseStrategy='share'. An empty branch's reset cutoff would hide
   *  the shared warehouse, so it must create its own. */
  BRANCH_EMPTY_REQUIRES_OWN_WAREHOUSE = 'BRANCH_EMPTY_REQUIRES_OWN_WAREHOUSE',
  /** Branch hard delete — the headquarters can never be deleted. */
  BRANCH_DELETE_HEADQUARTERS = 'BRANCH_DELETE_HEADQUARTERS',
  /** Branch hard delete — a business must keep at least one branch. */
  BRANCH_DELETE_LAST_BRANCH = 'BRANCH_DELETE_LAST_BRANCH',
  /** Branch hard delete — target missing, inactive, released, other business, or the source itself. */
  BRANCH_DELETE_INVALID_TARGET = 'BRANCH_DELETE_INVALID_TARGET',
  /** Branch hard delete — typed name does not match the branch name. */
  BRANCH_DELETE_CONFIRMATION_MISMATCH = 'BRANCH_DELETE_CONFIRMATION_MISMATCH',
  /** Branch hard delete — open shift / open restaurant account / draft invoice / pending e-CF (see `blockers`). */
  BRANCH_DELETE_BLOCKED = 'BRANCH_DELETE_BLOCKED',
  /** A write targeted a branch that was deleted (stale tab / device). */
  BRANCH_DELETED = 'BRANCH_DELETED',

  /** POST /operations/reset — confirmation body is missing or not the RESET-OPERATIONS keyword. */
  OPERATIONS_RESET_INVALID_CONFIRMATION = 'OPERATIONS_RESET_INVALID_CONFIRMATION',
  /** POST /operations/reset — resolved businessId does not match the UUID regex (defense-in-depth). */
  OPERATIONS_RESET_INVALID_ID = 'OPERATIONS_RESET_INVALID_ID',

  /** POST /ecf/certification/sadmin/approval/parse — uploaded XLSX is missing the ACEECF_Generadas sheet (operator uploaded the wrong file). */
  ECF_APPROVAL_XLSX_WRONG_SHEET = 'ECF_APPROVAL_XLSX_WRONG_SHEET',

  // Product cost management (cost-from-purchases feature)
  /** productCostRequired=true and product saved without a cost (explicit 0 is valid). */
  PRODUCT_COST_REQUIRED = 'PRODUCT_COST_REQUIRED',
  PRODUCT_IMAGE_MANAGE_NOT_PERMITTED = 'PRODUCT_IMAGE_MANAGE_NOT_PERMITTED',
  /** costFromPurchasesMode='last_purchase' — costPrice is system-owned; needs inventory.products.edit_cost (admins bypass). */
  PRODUCT_COST_EDIT_FORBIDDEN = 'PRODUCT_COST_EDIT_FORBIDDEN',
  /** Receiving items on a cancelled purchase document — stock/cost reality must never move. */
  PURCHASE_ORDER_CANCELLED_RECEIVE = 'PURCHASE_ORDER_CANCELLED_RECEIVE',
  /** A variant PARENT (grouping template, no stock of its own) was put on a
   *  purchase/invoice line. Only its child combinations are transactable. */
  PRODUCT_VARIANT_PARENT_NOT_TRANSACTABLE = 'PRODUCT_VARIANT_PARENT_NOT_TRANSACTABLE',

  // Restaurant errors
  RESTAURANT_TABLE_ALREADY_OPEN = 'RESTAURANT_TABLE_ALREADY_OPEN',
  // Payment-lock acquire conflict — another terminal holds a fresh
  // (within-TTL) payment lock on the same open account.
  RESTAURANT_ACCOUNT_PAYMENT_LOCKED = 'RESTAURANT_ACCOUNT_PAYMENT_LOCKED',
  RESTAURANT_DISCOUNT_BOTH_MODES = 'RESTAURANT_DISCOUNT_BOTH_MODES',

  // NCF errors
  NCF_SEQUENCE_DUPLICATE = 'NCF_SEQUENCE_DUPLICATE',
  NCF_SEQUENCE_EXHAUSTED = 'NCF_SEQUENCE_EXHAUSTED',
  // Per-branch NCF ranges (see invoicing/ncf-range-allocation spec).
  // Raised when assigning/extending a branch range that overlaps another
  // branch's range or the business-wide default for the same ncf_type.
  NCF_RANGE_OVERLAP = 'NCF_RANGE_OVERLAP',
  // Raised when a branch has its own range for a type but it is
  // missing/exhausted/expired and no shared default applies — reservation
  // fails clean instead of falling through to another range.
  NCF_NOT_CONFIGURED = 'NCF_NOT_CONFIGURED',
  // Raised when reassigning the owning branch of an NCF range that has
  // already issued numbers — re-scoping burned numbers is not allowed.
  NCF_RANGE_IN_USE = 'NCF_RANGE_IN_USE',
  // Raised when a per-branch split's segments do not exactly tile the
  // original range (gap, overlap, or not covering the full span).
  NCF_SPLIT_INVALID_TILING = 'NCF_SPLIT_INVALID_TILING',

  // Invoice free-items gate
  INVOICE_FREE_ITEMS_NOT_ALLOWED = 'INVOICE_FREE_ITEMS_NOT_ALLOWED',
  INVOICE_FREE_ITEM_MISSING_DESCRIPTION = 'INVOICE_FREE_ITEM_MISSING_DESCRIPTION',
  INVOICE_FREE_ITEM_INVALID_PRICE = 'INVOICE_FREE_ITEM_INVALID_PRICE',

  // Price floor guard — caller lacks `sales.items.price_below_min`
  // AND at least one line is below the customer's contracted floor
  // (per-customer tier or product sellingPrice). data.violations
  // carries { productCode, productDescription, submittedPrice,
  // floorPrice, floorSource, tierName? } per offending line.
  INVOICE_ITEM_PRICE_BELOW_MIN = 'INVOICE_ITEM_PRICE_BELOW_MIN',

  // Backdating — a document (invoice, PO, payment, …) dated earlier than
  // the current business day is a privileged action gated by the
  // `documents.backdate` permission, enforced centrally by the
  // BackdateInterceptor. Payload carries { attemptedDate, serverBusinessDate, field }.
  DOCUMENT_BACKDATE_NOT_ALLOWED = 'DOCUMENT_BACKDATE_NOT_ALLOWED',

  // Accounting — period lifecycle + journal entry guards
  ACCOUNTING_PERIOD_LOCKED = 'ACCOUNTING_PERIOD_LOCKED',
  ACCOUNTING_PERIOD_NOT_OPEN = 'ACCOUNTING_PERIOD_NOT_OPEN',
  ACCOUNTING_PERIOD_NOT_CLOSED_FOR_ADJUSTMENT = 'ACCOUNTING_PERIOD_NOT_CLOSED_FOR_ADJUSTMENT',
  ACCOUNTING_PERIOD_NOT_CLOSED = 'ACCOUNTING_PERIOD_NOT_CLOSED',
  ACCOUNTING_PERIOD_ALREADY_LOCKED = 'ACCOUNTING_PERIOD_ALREADY_LOCKED',
  ACCOUNTING_PERIOD_NOT_LOCKED = 'ACCOUNTING_PERIOD_NOT_LOCKED',
  ACCOUNTING_LOCK_MODE_STRICT = 'ACCOUNTING_LOCK_MODE_STRICT',
  ACCOUNTING_UNLOCK_REASON_REQUIRED = 'ACCOUNTING_UNLOCK_REASON_REQUIRED',
  ACCOUNTING_ADJUSTMENT_NOT_PERMITTED = 'ACCOUNTING_ADJUSTMENT_NOT_PERMITTED',
  ACCOUNTING_GRACE_DAYS_INVALID = 'ACCOUNTING_GRACE_DAYS_INVALID',
  ACCOUNTING_LOCK_MODE_INVALID = 'ACCOUNTING_LOCK_MODE_INVALID',

  // Accounting — fiscal year close lifecycle
  FISCAL_YEAR_NOT_OPEN = 'FISCAL_YEAR_NOT_OPEN',
  FISCAL_YEAR_NOT_CLOSED = 'FISCAL_YEAR_NOT_CLOSED',
  FISCAL_YEAR_NO_PERIODS = 'FISCAL_YEAR_NO_PERIODS',
  FISCAL_YEAR_CLOSING_JE_MISSING = 'FISCAL_YEAR_CLOSING_JE_MISSING',
  FISCAL_YEAR_RETAINED_EARNINGS_MISSING = 'FISCAL_YEAR_RETAINED_EARNINGS_MISSING',

  // Accounting — chart of accounts (COA)
  ACCOUNT_CODE_DUPLICATE = 'ACCOUNT_CODE_DUPLICATE',

  // Accounting — backfill engine
  ACCOUNTING_NOT_ENABLED = 'ACCOUNTING_NOT_ENABLED',
  ACCOUNTING_BACKFILL_NOT_CANCELLABLE = 'ACCOUNTING_BACKFILL_NOT_CANCELLABLE',

  // Accounting — fixed assets
  FIXED_ASSET_CATEGORY_CODE_DUPLICATE = 'FIXED_ASSET_CATEGORY_CODE_DUPLICATE',
  FIXED_ASSET_CATEGORY_NOT_FOUND = 'FIXED_ASSET_CATEGORY_NOT_FOUND',
  FIXED_ASSET_CATEGORY_ACCOUNTS_REQUIRED = 'FIXED_ASSET_CATEGORY_ACCOUNTS_REQUIRED',
  FIXED_ASSET_CATEGORY_ACCOUNTS_INCOMPLETE = 'FIXED_ASSET_CATEGORY_ACCOUNTS_INCOMPLETE',
  FIXED_ASSET_CODE_DUPLICATE = 'FIXED_ASSET_CODE_DUPLICATE',
  FIXED_ASSET_NOT_IN_SERVICE = 'FIXED_ASSET_NOT_IN_SERVICE',
  FIXED_ASSET_TRANSFER_NO_TARGET = 'FIXED_ASSET_TRANSFER_NO_TARGET',
  FIXED_ASSET_REVALUE_ZERO = 'FIXED_ASSET_REVALUE_ZERO',
  FIXED_ASSET_DATE_INVALID = 'FIXED_ASSET_DATE_INVALID',

  // Accounting — budgets
  BUDGET_NAME_DUPLICATE = 'BUDGET_NAME_DUPLICATE',
  BUDGET_NOT_DRAFT = 'BUDGET_NOT_DRAFT',
  BUDGET_TRANSITION_INVALID = 'BUDGET_TRANSITION_INVALID',

  // Bank account registry
  BANK_ACCOUNT_NOT_FOUND = 'BANK_ACCOUNT_NOT_FOUND',
  BANK_ACCOUNT_ARCHIVED = 'BANK_ACCOUNT_ARCHIVED',
  BANK_ACCOUNT_CODE_REQUIRED = 'BANK_ACCOUNT_CODE_REQUIRED',
  BANK_ACCOUNT_NAME_REQUIRED = 'BANK_ACCOUNT_NAME_REQUIRED',
  BANK_ACCOUNT_CODE_DUPLICATE = 'BANK_ACCOUNT_CODE_DUPLICATE',
  BANK_ACCOUNT_GL_NOT_FOUND = 'BANK_ACCOUNT_GL_NOT_FOUND',
  BANK_ACCOUNT_GL_NOT_POSTABLE = 'BANK_ACCOUNT_GL_NOT_POSTABLE',
  BANK_ACCOUNT_NO_GL_LINK = 'BANK_ACCOUNT_NO_GL_LINK',

  // Bank statement import
  BANK_STATEMENT_NOT_FOUND = 'BANK_STATEMENT_NOT_FOUND',
  BANK_STATEMENT_EMPTY = 'BANK_STATEMENT_EMPTY',
  BANK_STATEMENT_ROW_INVALID = 'BANK_STATEMENT_ROW_INVALID',
  BANK_STATEMENT_ROW_NO_AMOUNT = 'BANK_STATEMENT_ROW_NO_AMOUNT',
  BANK_STATEMENT_DATE_INVALID = 'BANK_STATEMENT_DATE_INVALID',
  BANK_STATEMENT_AMOUNT_INVALID = 'BANK_STATEMENT_AMOUNT_INVALID',

  // Bank reconciliation sessions
  BANK_RECONCILIATION_ALREADY_OPEN = 'BANK_RECONCILIATION_ALREADY_OPEN',
  BANK_RECON_LINE_MISMATCH = 'BANK_RECON_LINE_MISMATCH',
  BANK_RECON_LINE_ALREADY_MATCHED = 'BANK_RECON_LINE_ALREADY_MATCHED',
  BANK_RECON_LINE_NOT_MATCHED = 'BANK_RECON_LINE_NOT_MATCHED',
  BANK_RECON_NO_STATEMENT = 'BANK_RECON_NO_STATEMENT',
  BANK_RECON_UNMATCHED_LINES = 'BANK_RECON_UNMATCHED_LINES',
  BANK_RECON_LOCKED = 'BANK_RECON_LOCKED',

  // Customer (generic / system rows)
  GENERIC_CUSTOMER_READ_ONLY = 'GENERIC_CUSTOMER_READ_ONLY',

  // PR5 — per-entity GL routing
  EXPENSE_CATEGORY_NOT_FOUND = 'EXPENSE_CATEGORY_NOT_FOUND',
  EXPENSE_CATEGORY_ACCOUNT_NOT_FOUND = 'EXPENSE_CATEGORY_ACCOUNT_NOT_FOUND',
  EXPENSE_CATEGORY_ACCOUNT_NOT_POSTABLE = 'EXPENSE_CATEGORY_ACCOUNT_NOT_POSTABLE',
  STOCK_ADJUST_REASON_NOT_FOUND = 'STOCK_ADJUST_REASON_NOT_FOUND',
  STOCK_ADJUST_REASON_ACCOUNT_NOT_FOUND = 'STOCK_ADJUST_REASON_ACCOUNT_NOT_FOUND',
  STOCK_ADJUST_REASON_ACCOUNT_NOT_POSTABLE = 'STOCK_ADJUST_REASON_ACCOUNT_NOT_POSTABLE',
  ACCOUNTING_ROUTING_ACCOUNT_NOT_FOUND = 'ACCOUNTING_ROUTING_ACCOUNT_NOT_FOUND',
  ACCOUNTING_ROUTING_ACCOUNT_NOT_POSTABLE = 'ACCOUNTING_ROUTING_ACCOUNT_NOT_POSTABLE',
  JOURNAL_PREVIEW_NO_RULE = 'JOURNAL_PREVIEW_NO_RULE',
  JOURNAL_PREVIEW_EVENT_NOT_FOUND = 'JOURNAL_PREVIEW_EVENT_NOT_FOUND',
  JOURNAL_PREVIEW_FAILED = 'JOURNAL_PREVIEW_FAILED',
  RULES_ENGINE_PRODUCT_CATEGORY_NOT_FOUND = 'RULES_ENGINE_PRODUCT_CATEGORY_NOT_FOUND',
  RULES_ENGINE_PRODUCT_CATEGORY_CONFIG_MISSING = 'RULES_ENGINE_PRODUCT_CATEGORY_CONFIG_MISSING',
  RULES_ENGINE_PRODUCT_CATEGORY_CONFIG_INVALID = 'RULES_ENGINE_PRODUCT_CATEGORY_CONFIG_INVALID',
  RULES_ENGINE_PRODUCT_CATEGORY_PATH_MISSING = 'RULES_ENGINE_PRODUCT_CATEGORY_PATH_MISSING',
  RULES_ENGINE_EXPENSE_CATEGORY_NOT_FOUND = 'RULES_ENGINE_EXPENSE_CATEGORY_NOT_FOUND',
  RULES_ENGINE_EXPENSE_CATEGORY_PATH_MISSING = 'RULES_ENGINE_EXPENSE_CATEGORY_PATH_MISSING',
  RULES_ENGINE_STOCK_ADJUST_REASON_NOT_FOUND = 'RULES_ENGINE_STOCK_ADJUST_REASON_NOT_FOUND',
  RULES_ENGINE_STOCK_ADJUST_REASON_PATH_MISSING = 'RULES_ENGINE_STOCK_ADJUST_REASON_PATH_MISSING',

  // DGII compliance (PR — phase 1 + 2)
  SUPPLIER_PAYMENT_ISR_AMOUNT_INVALID = 'SUPPLIER_PAYMENT_ISR_AMOUNT_INVALID',
  SUPPLIER_PAYMENT_ISR_TYPE_MISSING = 'SUPPLIER_PAYMENT_ISR_TYPE_MISSING',
  SUPPLIER_PAYMENT_OVERPAYMENT = 'SUPPLIER_PAYMENT_OVERPAYMENT',
  SUPPLIER_PAYMENT_ALREADY_VOIDED = 'SUPPLIER_PAYMENT_ALREADY_VOIDED',
  SUPPLIER_PAYMENT_VOID_REASON_REQUIRED = 'SUPPLIER_PAYMENT_VOID_REASON_REQUIRED',
  // Gastos on credit (expense-credit-payment)
  EXPENSE_HAS_PAYMENTS = 'EXPENSE_HAS_PAYMENTS',
  EXPENSE_ALREADY_VOIDED = 'EXPENSE_ALREADY_VOIDED',
  EXPENSE_VOID_REASON_REQUIRED = 'EXPENSE_VOID_REASON_REQUIRED',
  EXPENSE_VOID_USE_ANNUL = 'EXPENSE_VOID_USE_ANNUL',
  EXPENSE_CREDIT_PROVIDER_REQUIRED = 'EXPENSE_CREDIT_PROVIDER_REQUIRED',
  EXPENSE_NOT_PAYABLE = 'EXPENSE_NOT_PAYABLE',
  EXPENSE_NOT_FOUND = 'EXPENSE_NOT_FOUND',
  SUPPLIER_PAYMENT_DUPLICATE_REFERENCE = 'SUPPLIER_PAYMENT_DUPLICATE_REFERENCE',
  SUPPLIER_PAYMENT_NOT_FOUND = 'SUPPLIER_PAYMENT_NOT_FOUND',
  SUPPLIER_INVOICE_NOT_FOUND = 'SUPPLIER_INVOICE_NOT_FOUND',
  SUPPLIER_INVOICE_NOT_PAYABLE = 'SUPPLIER_INVOICE_NOT_PAYABLE',
  SUPPLIER_INVOICE_PAYMENT_EXCEEDS_BALANCE = 'SUPPLIER_INVOICE_PAYMENT_EXCEEDS_BALANCE',
  SUPPLIER_CREDIT_NOTE_NOT_RECEIVABLE = 'SUPPLIER_CREDIT_NOTE_NOT_RECEIVABLE',
  SUPPLIER_CREDIT_NOTE_PARENT_CANCELLED = 'SUPPLIER_CREDIT_NOTE_PARENT_CANCELLED',
  PURCHASE_ORDER_ALREADY_CANCELLED = 'PURCHASE_ORDER_ALREADY_CANCELLED',
  INVOICE_ANNULMENT_REASON_INVALID = 'INVOICE_ANNULMENT_REASON_INVALID',
  INVOICE_ECF_ALREADY_SUBMITTED = 'INVOICE_ECF_ALREADY_SUBMITTED',
  INVOICE_ANNUL_NOT_PERMITTED = 'INVOICE_ANNUL_NOT_PERMITTED',
  NCF_EXHAUSTION_WARNING = 'NCF_EXHAUSTION_WARNING',

  // AR write-off — manual bad-debt / small-balance / dispute settlement
  INVOICE_NOT_FOUND = 'INVOICE_NOT_FOUND',
  INVOICE_ALREADY_PAID = 'INVOICE_ALREADY_PAID',
  // Mid-payment race: the operator confirmed against `expectedTotal`,
  // but the server-recomputed total from the (live) lines diverged —
  // a sibling terminal edited the account between modal-open and
  // submit. Reject so no invoice is created against the stale number.
  INVOICE_TOTAL_CHANGED = 'INVOICE_TOTAL_CHANGED',
  // DELETE /invoices/:id gate — hard-delete allowed for DRAFT only.
  // Every other status routes through credit-note reversal per
  // docs/rules/013-invoice-lifecycle.md.
  INVOICE_NOT_A_DRAFT_USE_CREDIT_NOTE = 'INVOICE_NOT_A_DRAFT_USE_CREDIT_NOTE',
  // Conduce item return / cancel gates.
  CONDUCE_NOT_A_CONDUCE = 'CONDUCE_NOT_A_CONDUCE',
  CONDUCE_NOT_EDITABLE = 'CONDUCE_NOT_EDITABLE',
  CONDUCE_ALREADY_CONVERTED = 'CONDUCE_ALREADY_CONVERTED',
  CONDUCE_ALREADY_CANCELLED = 'CONDUCE_ALREADY_CANCELLED',
  CONDUCE_RETURN_EXCEEDS_DISPATCHED = 'CONDUCE_RETURN_EXCEEDS_DISPATCHED',
  // POST /credit-notes gate — CN only valid against issued invoices
  // (not DRAFT, not CANCELLED). DRAFT has no NCF/AR/GL to reverse;
  // CANCELLED has nothing left to return.
  INVOICE_NOT_ELIGIBLE_FOR_CREDIT_NOTE = 'INVOICE_NOT_ELIGIBLE_FOR_CREDIT_NOTE',
  // Cashier-prints / seller_pending finalize flow guards.
  INVOICE_NOT_A_DRAFT = 'INVOICE_NOT_A_DRAFT',
  INVOICE_ALREADY_FINALIZED = 'INVOICE_ALREADY_FINALIZED',
  // Precondition on POST /invoices/:id/finalize — rejects a retry
  // whose prior attempt already stacked payment_allocation rows on
  // the DRAFT row (retry-loop guard from the FAC-1-0000940 incident).
  INVOICE_HAS_PENDING_PAYMENTS = 'INVOICE_HAS_PENDING_PAYMENTS',
  INVOICE_CANCELLED = 'INVOICE_CANCELLED',
  INVOICE_NO_OUTSTANDING = 'INVOICE_NO_OUTSTANDING',
  INVOICE_WRITE_OFF_EXCEEDS_OUTSTANDING = 'INVOICE_WRITE_OFF_EXCEEDS_OUTSTANDING',
  INVOICE_WRITE_OFF_INVALID_AMOUNT = 'INVOICE_WRITE_OFF_INVALID_AMOUNT',
  INVOICE_WRITE_OFF_INVALID_REASON = 'INVOICE_WRITE_OFF_INVALID_REASON',
  INVOICE_WRITE_OFF_COMMENT_REQUIRED = 'INVOICE_WRITE_OFF_COMMENT_REQUIRED',
  INVOICE_WRITE_OFF_COMMENT_TOO_LONG = 'INVOICE_WRITE_OFF_COMMENT_TOO_LONG',
  INVOICE_WRITE_OFF_POST_FAILED = 'INVOICE_WRITE_OFF_POST_FAILED',

  // Capability / quota (PR-10 / PR-11 / PR-12)
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  FEATURE_NOT_AVAILABLE = 'FEATURE_NOT_AVAILABLE',
  CAPABILITY_DENIED = 'CAPABILITY_DENIED',

  // Subscription Billing — Phase 1-9 of the billing migration. The
  // FE error map in apps/neocrm/src/app/shared/utils/error-message.utils.ts
  // localizes these into Spanish via HTTP_ERRORS.*.
  STRIPE_BILLING_UNAVAILABLE = 'STRIPE_BILLING_UNAVAILABLE',
  STRIPE_PM_ATTACHED_TO_DIFFERENT_CUSTOMER = 'STRIPE_PM_ATTACHED_TO_DIFFERENT_CUSTOMER',
  PLAN_NOT_BILLABLE = 'PLAN_NOT_BILLABLE',
  CHANGE_APPLY_FAILED = 'CHANGE_APPLY_FAILED',

  // Per-branch role permissions — a write (assign/remove/replace) must be
  // scoped to an active branch; null would wipe/insert across every branch.
  ROLE_PERMISSION_BRANCH_REQUIRED = 'ROLE_PERMISSION_BRANCH_REQUIRED',

  // Payment claims (customer-facing dispute flow)
  PAYMENT_CLAIM_REASON_REQUIRED = 'PAYMENT_CLAIM_REASON_REQUIRED',
  PAYMENT_CLAIM_INVALID_AMOUNT = 'PAYMENT_CLAIM_INVALID_AMOUNT',
  PAYMENT_CLAIM_PENDING_EXISTS = 'PAYMENT_CLAIM_PENDING_EXISTS',
  PAYMENT_CLAIM_NOT_FOUND = 'PAYMENT_CLAIM_NOT_FOUND',
  PAYMENT_CLAIM_ALREADY_REVIEWED = 'PAYMENT_CLAIM_ALREADY_REVIEWED',
  PAYMENT_CLAIM_REJECT_REASON_REQUIRED = 'PAYMENT_CLAIM_REJECT_REASON_REQUIRED',
  PAYMENT_CLAIM_NO_SUBSCRIPTION = 'PAYMENT_CLAIM_NO_SUBSCRIPTION',

  // Sales orders
  SALES_ORDER_DELIVERY_DATE_REQUIRED = 'SALES_ORDER_DELIVERY_DATE_REQUIRED',
  SALES_ORDER_PAID_IMMUTABLE = 'SALES_ORDER_PAID_IMMUTABLE',
  SALES_ORDER_ITEM_LOCKED = 'SALES_ORDER_ITEM_LOCKED',
  SALES_ORDER_HEADER_DISCOUNT_EXCEEDS_SUBTOTAL = 'SALES_ORDER_HEADER_DISCOUNT_EXCEEDS_SUBTOTAL',
  SALES_ORDER_HAS_INVOICE = 'SALES_ORDER_HAS_INVOICE',
  SALES_ORDER_SENT_TO_PRODUCTION = 'SALES_ORDER_SENT_TO_PRODUCTION',
  SALES_ORDER_COMMANDA_STARTED = 'SALES_ORDER_COMMANDA_STARTED',
  SALES_ORDER_DEPOSIT_ALLOCATION_MISMATCH = 'SALES_ORDER_DEPOSIT_ALLOCATION_MISMATCH',
  SALES_ORDER_DEPOSIT_METHOD_REQUIRED = 'SALES_ORDER_DEPOSIT_METHOD_REQUIRED',

  // e-CF batch processor (manual reassign / cron retry)
  ECF_INVOICE_NOT_FOUND = 'ECF_INVOICE_NOT_FOUND',
  ECF_NCF_TYPE_UNMAPPED = 'ECF_NCF_TYPE_UNMAPPED',
  ECF_NCF_SEQUENCE_EXHAUSTED = 'ECF_NCF_SEQUENCE_EXHAUSTED',
  ECF_REPAIR_ALREADY_ACCEPTED = 'ECF_REPAIR_ALREADY_ACCEPTED',
  ECF_REPAIR_PAID_INVOICE_DELTA = 'ECF_REPAIR_PAID_INVOICE_DELTA',
  // Resubmit guard: the e-CF is already accepted by DGII, so a
  // (re)submission is a terminal no-op success — the batch processor
  // reconciles the invoice to `accepted` instead of retrying / failing.
  ECF_ALREADY_ACCEPTED = 'ECF_ALREADY_ACCEPTED',
  // Guard at the e-CF orchestrator boundary. A B-series (traditional)
  // NCF is NOT electronic and must never be minted into or submitted as
  // an e-CF. Thrown by signOnly / processInvoiceEcf when the invoice
  // carries a non-E NCF — prevents a B01 from being coerced into E31 and
  // pushed to the DGII e-CF flow.
  ECF_NCF_NOT_ELECTRONIC = 'ECF_NCF_NOT_ELECTRONIC',
  // A credit/debit note (E33/E34) cannot be emitted because it has no
  // resolvable original invoice reference (InformacionReferencia): either
  // original_invoice_id is null, or the original invoice carries no NCF.
  // DGII requires NCFModificado, so the note is unemittable until linked.
  ECF_NOTE_MISSING_REFERENCE = 'ECF_NOTE_MISSING_REFERENCE',
  // POST /invoices create-time guards for debit notes (B03/E33). A debit
  // note MUST reference a pre-existing original invoice (DGII
  // InformacionReferencia), and the original's NCF series must match the
  // note's fiscal class (E-note → E-original, B-note → B-original).
  DEBIT_NOTE_REQUIRES_ORIGINAL = 'DEBIT_NOTE_REQUIRES_ORIGINAL',
  DEBIT_NOTE_ORIGINAL_NOT_FOUND = 'DEBIT_NOTE_ORIGINAL_NOT_FOUND',
  NOTE_ORIGINAL_SERIES_MISMATCH = 'NOTE_ORIGINAL_SERIES_MISMATCH',
  // A payment cannot be registered against an annulled (cancelled) invoice.
  INVOICE_CANCELLED_NO_PAYMENT = 'INVOICE_CANCELLED_NO_PAYMENT',
  // business-settings update rejects a manual flip to ecfEnabled=true
  // when the business has no active DGII certificate. Wizard's
  // activation step is the intended path — flipping the flag on
  // without a cert would only produce failing DGII submissions.
  ECF_ACTIVATION_REQUIRES_CERTIFICATE = 'ECF_ACTIVATION_REQUIRES_CERTIFICATE',
  // Certificate upload rejected because DGII authentication (semilla
  // → validarsemilla) failed with the uploaded P12 + passphrase.
  // Response payload carries the raw DGII message under `dgiiError`
  // so the FE "Ver más" button can surface the specific reason
  // (e.g. "El RNC ... del certificado no está delegado…").
  ECF_CERTIFICATE_DGII_AUTH_FAILED = 'ECF_CERTIFICATE_DGII_AUTH_FAILED',
  // Uploaded P12 could not be opened with the passphrase given (PKCS#12 MAC
  // check failed). Raised as a 400 so it is an operator fix, not a 500.
  ECF_CERTIFICATE_PASSPHRASE_INVALID = 'ECF_CERTIFICATE_PASSPHRASE_INVALID',
  // Uploaded file is not a readable P12 or holds no certificate.
  ECF_CERTIFICATE_FILE_INVALID = 'ECF_CERTIFICATE_FILE_INVALID',
  // business-settings update rejects a change of `ecfEnvironment` when
  // the target env has no active, non-expired certificate. Otherwise
  // the business would flip to an env whose DGII auth cannot succeed,
  // wedging the batch cron on retries + surfacing 401s to operators.
  ECF_ENVIRONMENT_REQUIRES_CERTIFICATE = 'ECF_ENVIRONMENT_REQUIRES_CERTIFICATE',

  // Posting rules engine — thrown when the resolved journal lines
  // don't balance (SUM debits != SUM credits) on a financial event.
  RULES_ENGINE_UNBALANCED = 'RULES_ENGINE_UNBALANCED',

  // Manual reconciliation of a parked financial event — thrown by
  // the reconcile endpoint when the operator submits lines whose
  // debit / credit totals don't match to the cent.
  RECONCILE_LINES_UNBALANCED = 'RECONCILE_LINES_UNBALANCED',
  // Thrown when a submitted line references an account that doesn't
  // exist in the tenant's chart of accounts, isn't postable (header),
  // or is archived.
  RECONCILE_ACCOUNT_INVALID = 'RECONCILE_ACCOUNT_INVALID',
  // Thrown by the reconcile endpoint when the target event is not in
  // `posting_status='parked'` with a `RULES_ENGINE_UNBALANCED` last
  // error — reconciliation only applies to that class of parked event.
  PARKED_EVENT_NOT_UNBALANCED = 'PARKED_EVENT_NOT_UNBALANCED',

  // Subscription lifecycle — thrown by SubscriptionStateGuard when
  // a write endpoint is hit by a business in past_due / suspended
  // state. FE translates via errorCodeMap + shows the pay CTA.
  SUBSCRIPTION_PAST_DUE = 'SUBSCRIPTION_PAST_DUE',
  SUBSCRIPTION_SUSPENDED = 'SUBSCRIPTION_SUSPENDED',
  /** Active sub but a collectible billing invoice is past due — the
   *  write interceptor blocks new e-CF issuance until it's settled. */
  SUBSCRIPTION_INVOICE_OVERDUE = 'SUBSCRIPTION_INVOICE_OVERDUE',

  // Plan-change guard — refuses a catalog plan change while the
  // subscription is past_due (≥1 unpaid billing_invoice). Operator
  // must settle the outstanding invoice before switching plans.
  CATALOG_SUBSCRIPTION_PAST_DUE = 'CATALOG_SUBSCRIPTION_PAST_DUE',

  // Billing cadence — refuses to change monthly ↔ annual while an
  // outstanding recurring invoice is still unpaid. Operator must
  // pay or void it first. Backend gate mirrors the FE lock on
  // BillingIntervalSection.
  BILLING_INTERVAL_LOCKED_UNPAID_INVOICE = 'BILLING_INTERVAL_LOCKED_UNPAID_INVOICE',
  /** A recurring invoice was requested for a zero-length or reversed period. */
  BILLING_INVALID_PERIOD = 'BILLING_INVALID_PERIOD',
  /** Capacity reduction refused — the tenant is still consuming the slots. */
  ADDON_REDUCTION_CAPACITY_IN_USE = 'ADDON_REDUCTION_CAPACITY_IN_USE',
  /** Capacity reduction refused — slotsToDrop must be > 0. */
  ADDON_REDUCTION_SLOTS_INVALID = 'ADDON_REDUCTION_SLOTS_INVALID',
  /** Capacity reduction refused — more slots requested than the business holds. */
  ADDON_REDUCTION_EXCEEDS_ACTIVE = 'ADDON_REDUCTION_EXCEEDS_ACTIVE',

  // Manual per-business recurring run refuses to emit a final invoice
  // for a subscription already flagged cancel_at_period_end — the
  // enforcement cron cancels it on the boundary, orphaning the invoice.
  BILLING_SUBSCRIPTION_CANCELLING = 'BILLING_SUBSCRIPTION_CANCELLING',

  // Collection-authority guard — the invoice is billed by a
  // provider-managed engine (Stripe Subscriptions / PayPal
  // Reference Transactions), so the local off-session charge
  // path refuses to act on it. Prevents the double-billing regression
  // where the local processor would fire alongside the provider's
  // own recurring cycle. See docs/knowledge/billing-collection.md.
  BILLING_COLLECTION_AUTHORITY_MISMATCH = 'BILLING_COLLECTION_AUTHORITY_MISMATCH',

  // Charge attempted on an invoice that already reached a paid /
  // void / failed state. Response payload carries paidAt +
  // receiptLink + nextPending so the FE can offer a useful
  // follow-up action instead of a generic "already paid" toast.
  BILLING_INVOICE_ALREADY_PAID = 'BILLING_INVOICE_ALREADY_PAID',
  BILLING_ISSUED_AT_IN_FUTURE = 'BILLING_ISSUED_AT_IN_FUTURE',
  INVOICE_CREDIT_TERM_UNKNOWN = 'INVOICE_CREDIT_TERM_UNKNOWN',

  // Another PaymentIntent for the same invoice is in-flight
  // (either a cron retry mid-charge, or another operator still
  // typing card details). Blocked to prevent double-charge. Retry
  // after the 15 s window closes.
  BILLING_PAYMENT_IN_FLIGHT = 'BILLING_PAYMENT_IN_FLIGHT',

  // Applying a plan downgrade would leave the business above the
  // target plan's limits. Response carries a `breaches` array so the
  // FE can name which resource is over (users, branches, …).
  CHANGE_REQUEST_COMPLIANCE_BLOCKED = 'CHANGE_REQUEST_COMPLIANCE_BLOCKED',

  // Prepayment refused because the subscription is provider-managed
  // (Stripe / PayPal off-session recurring). The provider owns
  // money movement; sadmin manual entry would double-collect.
  PREPAYMENT_PROVIDER_MANAGED = 'PREPAYMENT_PROVIDER_MANAGED',
  // Prepayment refused because an unpaid recurring invoice already
  // exists for the current period. Operator must resolve that
  // invoice first (mark paid, void, or adjust) before recording a
  // prepayment for the next period.
  PREPAYMENT_OVERLAPPING_INVOICE = 'PREPAYMENT_OVERLAPPING_INVOICE',
  // Prepayment monthsCount must be an integer in [1, 24]. Prevents
  // typos / accidental 0 / negative / >2yr entries.
  PREPAYMENT_INVALID_MONTHS = 'PREPAYMENT_INVALID_MONTHS',
  // Prepayment refused because the subscription is flagged
  // cancel_at_period_end — the sub will be cancelled at the current
  // boundary, so prepaying beyond it would create a paid invoice
  // for a period the customer no longer has access to.
  PREPAYMENT_SUBSCRIPTION_CANCELLING = 'PREPAYMENT_SUBSCRIPTION_CANCELLING',
  // Customer-facing "Pay Now (with prepayment)" — the saved card
  // charge on the freshly-materialized prepayment invoice failed.
  // Invoice remains OPEN; sub period NOT advanced; dunning cadence
  // handles retries. Response payload carries the invoice + Stripe
  // failure reason so the FE toast can point at the next action.
  PREPAYMENT_CARD_FAILED = 'PREPAYMENT_CARD_FAILED',
  // Customer-facing "Pay Now" — refused because no saved payment
  // method exists on the subscription. Customer must enroll a card
  // on the Métodos de Pago section before prepaying.
  PREPAYMENT_NO_PAYMENT_METHOD = 'PREPAYMENT_NO_PAYMENT_METHOD',
  // Customer-facing "Pay Now" — monthsCount not in the allowed set
  // [1, 3, 6, 12]. The DTO validator already blocks other values but
  // the service throws this so mobile / direct API callers get the
  // same normalized error code.
  PREPAYMENT_INVALID_MONTHS_COUNT = 'PREPAYMENT_INVALID_MONTHS_COUNT',

  // Credit-note fiscal-integrity gates. Fired when the operator
  // tries to reverse an invoice that has an NCF but the business's
  // `credit_note` invoice_sequence row is misconfigured. Silent
  // fallback used to produce a NOT-…-… document that DGII could
  // not accept (Meran Rodamientos y Repuestos incident, 2026-07-28
  // — see migration 0616). Backend now hard-rejects; FE surfaces
  // the mapped Spanish message so the operator knows to configure
  // the correct fiscal sequence in sadmin before retrying.
  CREDIT_NOTE_SEQUENCE_NOT_CONFIGURED = 'CREDIT_NOTE_SEQUENCE_NOT_CONFIGURED',
  CREDIT_NOTE_SEQUENCE_MISSING_ETYPE = 'CREDIT_NOTE_SEQUENCE_MISSING_ETYPE',
  CREDIT_NOTE_SEQUENCE_MISMATCH_B_TYPE = 'CREDIT_NOTE_SEQUENCE_MISMATCH_B_TYPE',
  // A credit note can only be an electronic comprobante (E34) when the
  // invoice it reverses is itself an e-CF (E-series NCF). Thrown only when a
  // caller EXPLICITLY requires an electronic credit note (requireElectronicNcf)
  // against an original that has no E-series NCF (non-fiscal / pre-factura).
  // The default path silently downgrades to a non-fiscal NOT- credit note.
  CREDIT_NOTE_ORIGINAL_NOT_FISCAL = 'CREDIT_NOTE_ORIGINAL_NOT_FISCAL',

  // DGII e-CF Formato v1.0 sección A campo 38 — E32 (Factura de
  // Consumo Electrónica) with MontoTotal ≥ DOP$250,000 must carry
  // RNCComprador (or IdentificadorExtranjero for foreign buyers).
  // Pre-submit guard in invoice-to-ecf-mapper avoids the DGII 4xx.
  ECF_E32_LARGE_AMOUNT_REQUIRES_RNC = 'ECF_E32_LARGE_AMOUNT_REQUIRES_RNC',

  // Bulk invoice import — validator rejects at row level:
  //   * QUOTE type is still stubbed pending quote-creation wire-up;
  //     backend rejects to avoid the row being silently dropped.
  //   * FISCAL_CREDIT requires customerRnc (B01/E31 DGII rule).
  //   * Invoice-type value not in VALID_INVOICE_TYPES.
  INVOICE_BULK_IMPORT_QUOTE_NOT_SUPPORTED_YET = 'INVOICE_BULK_IMPORT_QUOTE_NOT_SUPPORTED_YET',
  INVOICE_BULK_IMPORT_RNC_REQUIRED_FOR_FISCAL_CREDIT = 'INVOICE_BULK_IMPORT_RNC_REQUIRED_FOR_FISCAL_CREDIT',
  INVOICE_BULK_IMPORT_INVALID_TYPE = 'INVOICE_BULK_IMPORT_INVALID_TYPE',

  // Bulk invoice import — recovery / resume errors:
  //   * DATA_CORRUPTED: processed_data column can't be parsed or is
  //     empty/malformed → resume marks the job FAILED so the operator
  //     starts a fresh import instead of retrying against garbage.
  //   * CANNOT_RESUME: caller invoked resume on a job whose status
  //     isn't PROCESSING (already completed, failed, cancelled).
  //   * NO_ITEMS_TO_PROCESS: processedData parsed empty; treat as
  //     a corrupted job.
  INVOICE_BULK_IMPORT_DATA_CORRUPTED = 'INVOICE_BULK_IMPORT_DATA_CORRUPTED',
  INVOICE_BULK_IMPORT_CANNOT_RESUME = 'INVOICE_BULK_IMPORT_CANNOT_RESUME',
  INVOICE_BULK_IMPORT_NO_ITEMS_TO_PROCESS = 'INVOICE_BULK_IMPORT_NO_ITEMS_TO_PROCESS',

  // Credit invoicing errors — thrown by invoices.service.ts when the
  // cashier attempts a non-contado invoice against a customer with no
  // stored creditLimit, or when server-side ceiling enforcement
  // requires an approval token that is missing / invalid. Mirrored on
  // FE (errorCodeMap.ts) so the operator gets a Spanish toast instead
  // of a raw English exception.
  CREDIT_LIMIT_REQUIRED_NOT_SET = 'CREDIT_LIMIT_REQUIRED_NOT_SET',
  CREDIT_APPROVAL_REQUIRED = 'CREDIT_APPROVAL_REQUIRED',
  CREDIT_APPROVAL_INVALID = 'CREDIT_APPROVAL_INVALID',

  // Product field-level edit gate — thrown by ProductService when a
  // caller with `inventory.products.edit` attempts to mutate a field
  // whose EDIT_COLUMN_GROUP_* twin they lack (sellingPrice / costPrice
  // / providerName / etc.). Response payload carries { field,
  // requiredPerm } so the FE toast can be specific.
  PRODUCTS_EDIT_FIELD_FORBIDDEN = 'PRODUCTS_EDIT_FIELD_FORBIDDEN',

  // Tax catalog — default-tax invariant guards.
  //   * TAX_DEFAULT_CANNOT_DELETE: DELETE /taxes/:id refused because the
  //     row is the business's default tax. Operator must set another row
  //     as default first (POST /taxes/:id/set-default).
  //   * TAX_DEFAULT_MISSING: resolver could not find a default tax for
  //     the business. Should never happen post-seed — indicates data
  //     drift (manual DB delete, bad migration).
  //   * TAX_SERVICE_CHARGE_CANNOT_DELETE: DELETE /taxes/:id refused
  //     because the row is the tenant's service-charge (Propina Legal)
  //     tax. Propina is load-bearing across products, invoice, POS,
  //     restaurant AND /reports/propina + vendor commission payouts, and
  //     an UNUSED row would be HARD deleted (unrecoverable) — leaving the
  //     tenant with no service_charge tax and a propina report that reads
  //     RD$0.00 forever with nothing to point at. Operator deactivates
  //     the tax instead (it stays restorable via PATCH /taxes/:id/activate).
  TAX_DEFAULT_CANNOT_DELETE = 'TAX_DEFAULT_CANNOT_DELETE',
  TAX_DEFAULT_MISSING = 'TAX_DEFAULT_MISSING',
  TAX_SERVICE_CHARGE_CANNOT_DELETE = 'TAX_SERVICE_CHARGE_CANNOT_DELETE',
  // Rate change on a canonical DGII code (ITBIS_18/ITBIS_16) refused
  // because tenant has e-CF enabled. DGII wire format requires the
  // fixed rate for the bucket — operator must disable e-CF (or contact
  // DGII for a new tax code) before changing the rate.
  TAX_RATE_CHANGE_BLOCKED_ECF = 'TAX_RATE_CHANGE_BLOCKED_ECF',
  // Refused because another active tax on the business already carries
  // the requested numeric rate. Prevents "IVA 18%" alongside "ITBIS 18%"
  // — every downstream picker / report / GL groups by (taxId, rate)
  // bucket, so two rows at the same rate cross-contaminate.
  TAX_RATE_DUPLICATE = 'TAX_RATE_DUPLICATE',
  // Rate change refused on a canonical DGII code (ITBIS_18/16/EXEMPT/
  // NON_TAXABLE) — fixed rates by fiscal identifier. Historical
  // reports would cross-contaminate + DGII wire format would break.
  // Operator must create a new tax with the new rate + deprecate the
  // old row.
  TAX_CANONICAL_RATE_IMMUTABLE = 'TAX_CANONICAL_RATE_IMMUTABLE',

  // Product-level included-taxes (Propina/Servicio baked into the
  // shelf price). Rejected combinations:
  //   * PRODUCT_INCLUDED_TAX_REQUIRES_TAX_INCLUDED — includedTaxIds
  //     supplied but taxIncluded=false. The list is meaningless when
  //     the price is entered net; refuse rather than silently drop.
  //   * PRODUCT_INCLUDED_TAX_DUPLICATES_MAIN_TAX — a taxId in the
  //     included list matches the product's main taxId. Would
  //     double-count the same tax.
  //   * PRODUCT_INCLUDED_TAX_NOT_AVAILABLE — the referenced tax row
  //     is missing OR has availableAsIncludedTax=false. Ops must opt
  //     the tax in from /configuration/tax first.
  PRODUCT_INCLUDED_TAX_REQUIRES_TAX_INCLUDED = 'PRODUCT_INCLUDED_TAX_REQUIRES_TAX_INCLUDED',
  PRODUCT_INCLUDED_TAX_DUPLICATES_MAIN_TAX = 'PRODUCT_INCLUDED_TAX_DUPLICATES_MAIN_TAX',
  PRODUCT_INCLUDED_TAX_NOT_AVAILABLE = 'PRODUCT_INCLUDED_TAX_NOT_AVAILABLE',
  // A product with the same SKU already exists in the same
  // (business, branch) scope. SKU uniqueness is per-branch; blank
  // SKU is exempt (treated as "no SKU").
  PRODUCT_SKU_DUPLICATE = 'PRODUCT_SKU_DUPLICATE',
  /** A variant child removed on this save is still used as a recipe
   *  ingredient, so the hard delete would violate the RESTRICT FK on
   *  product_recipe_ingredient. */
  PRODUCT_VARIANT_IN_RECIPE = 'PRODUCT_VARIANT_IN_RECIPE',
  // GET /images/fetch-remote could not proxy the chosen suggestion image
  // (host not allowlisted, not https, oversized, or the remote failed).
  IMAGE_REMOTE_FETCH_FAILED = 'IMAGE_REMOTE_FETCH_FAILED',
  // POST /images/suggestions/import could not fetch/decode/store the picked
  // image into the shared bank (SSRF reject, non-image, or S3 failure).
  IMAGE_BANK_IMPORT_FAILED = 'IMAGE_BANK_IMPORT_FAILED',
  // Two presentations on the same product share a unit without distinct
  // names → violates idx_product_unit_unique_unit_name (business,
  // product, unit, lower(coalesce(name,''))). Name each uniquely.
  PRODUCT_PRESENTATION_DUPLICATE = 'PRODUCT_PRESENTATION_DUPLICATE',

  // ─── Sales commission / vendors ───
  //   * SALES_VENDOR_NAME_REQUIRED — neither a linked user nor a name
  //     was supplied for a standalone vendor.
  //   * SALES_VENDOR_DUPLICATE_NAME — a vendor with that name already
  //     exists in the same (business, branch) scope.
  //   * SALES_VENDOR_NOT_FOUND — vendor id not found for this business.
  //   * SALES_VENDOR_NOT_ASSIGNABLE_TO_BRANCH — a branch-scoped vendor
  //     cannot be assigned to a sale in a different branch.
  //   * SALES_VENDOR_INACTIVE — the vendor is deactivated and cannot be
  //     newly assigned.
  SALES_VENDOR_NAME_REQUIRED = 'SALES_VENDOR_NAME_REQUIRED',
  SALES_VENDOR_DUPLICATE_NAME = 'SALES_VENDOR_DUPLICATE_NAME',
  SALES_VENDOR_NOT_FOUND = 'SALES_VENDOR_NOT_FOUND',
  SALES_VENDOR_NOT_ASSIGNABLE_TO_BRANCH = 'SALES_VENDOR_NOT_ASSIGNABLE_TO_BRANCH',
  SALES_VENDOR_INACTIVE = 'SALES_VENDOR_INACTIVE',
  //   * INVOICE_VENDOR_LOCKED_AFTER_CREDIT_NOTE — the sale already has a
  //     credit note (or payout), so its vendor can no longer be reassigned.
  INVOICE_VENDOR_LOCKED_AFTER_CREDIT_NOTE = 'INVOICE_VENDOR_LOCKED_AFTER_CREDIT_NOTE',
  //   * SALES_VENDOR_ASSIGN_FORBIDDEN — explicitly picking a vendor at
  //     checkout requires the sales.vendor.assign permission.
  SALES_VENDOR_ASSIGN_FORBIDDEN = 'SALES_VENDOR_ASSIGN_FORBIDDEN',

  // Cron control panel — sadmin PUT /sadmin/crons/:name/schedule
  //   * CRON_SCHEDULE_CONFLICT — expectedUpdatedAt mismatch (409).
  //     Other admin edited the same row; FE toasts refresh + refetches.
  //   * CRON_SCHEDULE_INTERVAL_TOO_LOW — parsed expression fires more
  //     often than the cron's `minIntervalSeconds` allows (422).
  //   * CRON_SCHEDULE_CUSTOM_NOT_ALLOWED — cron has
  //     `allowCustomExpression=false`; sadmin must pick a preset (422).
  //   * CRON_SCHEDULE_INVALID_EXPRESSION — cron-parser rejected the
  //     expression (422). Preset picker prevents this in the happy path.
  //   * CRON_UNKNOWN — cron_name not in CRON_MANIFEST (404).
  CRON_SCHEDULE_CONFLICT = 'CRON_SCHEDULE_CONFLICT',
  CRON_SCHEDULE_INTERVAL_TOO_LOW = 'CRON_SCHEDULE_INTERVAL_TOO_LOW',
  CRON_SCHEDULE_CUSTOM_NOT_ALLOWED = 'CRON_SCHEDULE_CUSTOM_NOT_ALLOWED',
  CRON_SCHEDULE_INVALID_EXPRESSION = 'CRON_SCHEDULE_INVALID_EXPRESSION',
  CRON_UNKNOWN = 'CRON_UNKNOWN',

  // Reseller platform (v3 plan)
  //   * RESELLER_EMAIL_ALREADY_EXISTS — case-insensitive uniqueness on
  //     reseller_account.contact_email (409).
  //   * RESELLER_NOT_FOUND — id missing or does not exist (404).
  //   * ASSIGNMENT_PERIOD_OVERLAP — EXCLUDE gist constraint rejected an
  //     assignment whose [active_from, active_to) overlaps another
  //     active row for the same business (409).
  //   * ASSIGNMENT_IMMUTABLE_USE_VERSIONED_UPDATE — PATCH on an
  //     assignment row is banned; end the current row + POST a new one
  //     via `POST /sadmin/resellers/:id/assignments` (400).
  //   * ASSIGNMENT_NOT_FOUND — assignment id missing (404).
  //   * ASSIGNMENT_ALREADY_ENDED — POST /end on an assignment whose
  //     active_to is already set (409).
  //   * BACKDATE_NOT_PERMITTED — activeFrom in the past requires the
  //     sadmin.resellers.backdate_assignment permission (403).
  //   * RESELLER_STATUS_TRANSITION_INVALID — suspend/reactivate on the
  //     wrong current status (409).
  RESELLER_EMAIL_ALREADY_EXISTS = 'RESELLER_EMAIL_ALREADY_EXISTS',
  //   * RESELLER_USERNAME_ALREADY_EXISTS — sadmin picked a
  //     default_username that clashes with another reseller row (409).
  RESELLER_USERNAME_ALREADY_EXISTS = 'RESELLER_USERNAME_ALREADY_EXISTS',
  //   * RESELLER_INVITATION_NOT_FOUND — GET
  //     /reseller/auth/invitation-preview called with an unknown or
  //     already-consumed token (404).
  RESELLER_INVITATION_NOT_FOUND = 'RESELLER_INVITATION_NOT_FOUND',
  //   * RESELLER_INVITATION_EXPIRED — token was valid at some point but
  //     the 7-day TTL has passed. Sadmin can reissue via /invitations/resend
  //     (410).
  RESELLER_INVITATION_EXPIRED = 'RESELLER_INVITATION_EXPIRED',
  //   * RESELLER_USER_NOT_ACCEPTED_YET — sadmin tried to reset password
  //     for a reseller who hasn't accepted the invitation yet. Direct
  //     the operator to resend the invitation instead (404).
  RESELLER_USER_NOT_ACCEPTED_YET = 'RESELLER_USER_NOT_ACCEPTED_YET',
  //   * RESELLER_USER_DISABLED — reseller user row exists but status !=1.
  //     Reactivate first before dispatching a reset link (400).
  RESELLER_USER_DISABLED = 'RESELLER_USER_DISABLED',
  RESELLER_NOT_FOUND = 'RESELLER_NOT_FOUND',
  ASSIGNMENT_PERIOD_OVERLAP = 'ASSIGNMENT_PERIOD_OVERLAP',
  ASSIGNMENT_IMMUTABLE_USE_VERSIONED_UPDATE = 'ASSIGNMENT_IMMUTABLE_USE_VERSIONED_UPDATE',
  ASSIGNMENT_NOT_FOUND = 'ASSIGNMENT_NOT_FOUND',
  ASSIGNMENT_ALREADY_ENDED = 'ASSIGNMENT_ALREADY_ENDED',
  BACKDATE_NOT_PERMITTED = 'BACKDATE_NOT_PERMITTED',
  RESELLER_STATUS_TRANSITION_INVALID = 'RESELLER_STATUS_TRANSITION_INVALID',
  //   * RESELLER_ALREADY_ARCHIVED — DELETE hit an already-archived
  //     reseller (409). Kept distinct from RESELLER_NOT_FOUND because
  //     the row still exists.
  RESELLER_ALREADY_ARCHIVED = 'RESELLER_ALREADY_ARCHIVED',
  //   * CONTRACT_NOT_ACCEPTED — reseller has no acceptance row for the
  //     currently-current reseller_contract_version (403). Guard on
  //     every /reseller/* feature route.
  CONTRACT_NOT_ACCEPTED = 'CONTRACT_NOT_ACCEPTED',
  //   * RESELLER_NOT_ASSIGNED — reseller attempted to impersonate a
  //     business they do not currently own (403).
  //   * RESELLER_IMPERSONATION_NOT_ALLOWED — a reseller-impersonated
  //     JWT hit an endpoint outside the allowlist for
  //     reseller_impersonation_v1 (403).
  RESELLER_NOT_ASSIGNED = 'RESELLER_NOT_ASSIGNED',
  RESELLER_IMPERSONATION_NOT_ALLOWED = 'RESELLER_IMPERSONATION_NOT_ALLOWED',

  // Recipes / raw materials
  //   * RECIPE_INGREDIENT_MUST_BE_STOCK_TRACKED — attempted to attach a
  //     service product as an ingredient. Only `good` + `raw_material`
  //     can be ingredients.
  //   * RECIPE_CANNOT_REFERENCE_SELF — recipe listed its own product
  //     as an ingredient.
  //   * RECIPE_INGREDIENT_CANNOT_BE_RECIPE — Phase 1 hard invariant:
  //     ingredient product cannot itself have an attached recipe.
  //     Lifted in Phase 2 with proper cycle detection.
  //   * RECIPE_INGREDIENT_UNIT_MISMATCH_PHASE_1 — consumptionUnit
  //     must equal the ingredient product's unitOfMeasure until
  //     Phase 2 introduces cross-unit conversion.
  //   * RECIPE_INGREDIENT_NOT_FOUND — an ingredient productId does
  //     not resolve to a product row in the tenant.
  //   * RECIPE_COST_EXCEEDS_PRICE_WARN — attached to the save
  //     response (as `warnings[]`, never thrown) when sum(cost) /
  //     yieldQty > sellingPrice.
  //   * RECIPE_COST_EXCEEDS_PRICE_BLOCKED — thrown from invoice
  //     submit when `business_settings.recipe_block_sale_below_recipe_cost`
  //     is true and any recipe line has costPerYieldedUnit > price.
  RECIPE_INGREDIENT_MUST_BE_STOCK_TRACKED = 'RECIPE_INGREDIENT_MUST_BE_STOCK_TRACKED',
  RECIPE_CANNOT_REFERENCE_SELF = 'RECIPE_CANNOT_REFERENCE_SELF',
  RECIPE_INGREDIENT_CANNOT_BE_RECIPE = 'RECIPE_INGREDIENT_CANNOT_BE_RECIPE',
  RECIPE_INGREDIENT_UNIT_MISMATCH_PHASE_1 = 'RECIPE_INGREDIENT_UNIT_MISMATCH_PHASE_1',
  RECIPE_INGREDIENT_NOT_FOUND = 'RECIPE_INGREDIENT_NOT_FOUND',
  RECIPE_COST_EXCEEDS_PRICE_WARN = 'RECIPE_COST_EXCEEDS_PRICE_WARN',
  RECIPE_COST_EXCEEDS_PRICE_BLOCKED = 'RECIPE_COST_EXCEEDS_PRICE_BLOCKED',

  // Procurement PO
  PROCUREMENT_PO_NO_ITEMS = 'PROCUREMENT_PO_NO_ITEMS',
  PROCUREMENT_PO_BRANCH_REQUIRED = 'PROCUREMENT_PO_BRANCH_REQUIRED',

  // Bulk import — clients + providers (with opening balance).
  // NOTE: the backend importer fork may also add these; keep the names
  // identical so the parent can dedupe trivially.
  CUSTOMER_BULK_IMPORT_ROW_DUPLICATE = 'CUSTOMER_BULK_IMPORT_ROW_DUPLICATE',
  CUSTOMER_OPENING_BALANCE_REFERENCE_REQUIRED = 'CUSTOMER_OPENING_BALANCE_REFERENCE_REQUIRED',
  CUSTOMER_BULK_IMPORT_ALREADY_PROCESSING = 'CUSTOMER_BULK_IMPORT_ALREADY_PROCESSING',
  PROVIDER_BULK_IMPORT_ROW_DUPLICATE = 'PROVIDER_BULK_IMPORT_ROW_DUPLICATE',
  PROVIDER_OPENING_BALANCE_REFERENCE_REQUIRED = 'PROVIDER_OPENING_BALANCE_REFERENCE_REQUIRED',
  PROVIDER_BULK_IMPORT_ALREADY_PROCESSING = 'PROVIDER_BULK_IMPORT_ALREADY_PROCESSING',

  // Contact merge (dedupe two customers / providers into one)
  CUSTOMER_MERGE_INTO_SELF = 'CUSTOMER_MERGE_INTO_SELF',
  CUSTOMER_MERGE_NOT_FOUND = 'CUSTOMER_MERGE_NOT_FOUND',
  CUSTOMER_MERGE_GENERIC_FORBIDDEN = 'CUSTOMER_MERGE_GENERIC_FORBIDDEN',
  PROVIDER_MERGE_INTO_SELF = 'PROVIDER_MERGE_INTO_SELF',
  PROVIDER_MERGE_NOT_FOUND = 'PROVIDER_MERGE_NOT_FOUND',
  // Hard-delete refused — the contact still has attached records.
  CUSTOMER_DELETE_HAS_RECORDS = 'CUSTOMER_DELETE_HAS_RECORDS',
  PROVIDER_DELETE_HAS_RECORDS = 'PROVIDER_DELETE_HAS_RECORDS',

  // Generic errors
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  BAD_REQUEST = 'BAD_REQUEST',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  PASSWORD_TOO_WEAK = 'PASSWORD_TOO_WEAK',

  // Rentals (Alquileres)
  RENTAL_NOT_FOUND = 'RENTAL_NOT_FOUND',
  RENTAL_INVALID_TRANSITION = 'RENTAL_INVALID_TRANSITION',
  RENTAL_LOCKED_IN_PRODUCTION = 'RENTAL_LOCKED_IN_PRODUCTION',
  RENTAL_LOCKED_PAID = 'RENTAL_LOCKED_PAID',
  RENTAL_COMMANDA_STARTED = 'RENTAL_COMMANDA_STARTED',
  RENTAL_FIANZA_REQUIRED = 'RENTAL_FIANZA_REQUIRED',
  RENTAL_FIANZA_MISMATCH = 'RENTAL_FIANZA_MISMATCH',
  RENTAL_FIANZA_EXCEEDS_TOTAL = 'RENTAL_FIANZA_EXCEEDS_TOTAL',
  RENTAL_ZERO_PRICE_FORBIDDEN = 'RENTAL_ZERO_PRICE_FORBIDDEN',
  RENTAL_LATE_REFUND_FORBIDDEN = 'RENTAL_LATE_REFUND_FORBIDDEN',
  RENTAL_RETURN_DATE_REQUIRED = 'RENTAL_RETURN_DATE_REQUIRED',
  RENTAL_ALREADY_RETURNED = 'RENTAL_ALREADY_RETURNED',
  RENTAL_DAMAGE_ADJUST_NOT_PENDING = 'RENTAL_DAMAGE_ADJUST_NOT_PENDING',
  RENTAL_DAMAGE_ADJUST_INVALID_AMOUNT = 'RENTAL_DAMAGE_ADJUST_INVALID_AMOUNT',
  RENTAL_DELIVERY_REQUIRES_FULL_PAYMENT = 'RENTAL_DELIVERY_REQUIRES_FULL_PAYMENT',
  RENTAL_ABONO_PENDING = 'RENTAL_ABONO_PENDING',
  RENTAL_ADD_ITEMS_INVALID_STATE = 'RENTAL_ADD_ITEMS_INVALID_STATE',
  RENTAL_ADD_ITEMS_LOCKED_INVOICED = 'RENTAL_ADD_ITEMS_LOCKED_INVOICED',
  RENTAL_ADD_ITEMS_EMPTY = 'RENTAL_ADD_ITEMS_EMPTY',
  RENTAL_SETTLEMENT_UNRESOLVED = 'RENTAL_SETTLEMENT_UNRESOLVED',
  RENTAL_NO_REFUNDABLE_DEPOSIT = 'RENTAL_NO_REFUNDABLE_DEPOSIT',
  RENTAL_NO_ACTIVE_SHIFT = 'RENTAL_NO_ACTIVE_SHIFT',
  RENTAL_STOCK_INSUFFICIENT = 'RENTAL_STOCK_INSUFFICIENT',
  RENTAL_ALREADY_INVOICED = 'RENTAL_ALREADY_INVOICED',
  RENTAL_NOT_READY_FOR_INVOICE = 'RENTAL_NOT_READY_FOR_INVOICE',
  RENTAL_PICKUP_TOKEN_EXPIRED = 'RENTAL_PICKUP_TOKEN_EXPIRED',
  RENTAL_PICKUP_TOKEN_INVALID = 'RENTAL_PICKUP_TOKEN_INVALID',
  RENTAL_PICKUP_NO_EMAIL = 'RENTAL_PICKUP_NO_EMAIL',
  RENTAL_PICKUP_NO_PHONE = 'RENTAL_PICKUP_NO_PHONE',
  RENTAL_CUSTOMER_REQUIRED = 'RENTAL_CUSTOMER_REQUIRED',
  RENTAL_CANCEL_REQUIRES_CREDIT_NOTE = 'RENTAL_CANCEL_REQUIRES_CREDIT_NOTE',

  // Rentals — Reservations
  RENTAL_RESERVATION_NOT_FOUND = 'RENTAL_RESERVATION_NOT_FOUND',
  RENTAL_RESERVATION_EXPIRED = 'RENTAL_RESERVATION_EXPIRED',
  RENTAL_RESERVATION_ALREADY_CONVERTED = 'RENTAL_RESERVATION_ALREADY_CONVERTED',
  RENTAL_AVAILABILITY_CONFLICT = 'RENTAL_AVAILABILITY_CONFLICT',
  // Delivery tax configuration
  DELIVERY_TAX_INVALID = 'DELIVERY_TAX_INVALID',

  // Sadmin billing "Ajustar y liquidar" (adjust & settle) orchestration.
  // Preconditions are reported by the dry-run preview before any mutation;
  // ADJUST_SETTLE_STEP_FAILED is thrown mid-execution and carries the failed
  // step name in its message/payload.
  NO_RESELLER_ASSIGNMENT = 'NO_RESELLER_ASSIGNMENT',
  CURRENCY_UNRESOLVED = 'CURRENCY_UNRESOLVED',
  INVOICE_NOT_VOIDABLE = 'INVOICE_NOT_VOIDABLE',
  CONFLICTING_ACTIVE_ANNUAL_SUB = 'CONFLICTING_ACTIVE_ANNUAL_SUB',
  ADJUST_SETTLE_STEP_FAILED = 'ADJUST_SETTLE_STEP_FAILED',
  ZERO_COMMISSION_BALANCE = 'ZERO_COMMISSION_BALANCE',
  // Execution recomputed an annual total that no longer matches the figure
  // the operator confirmed in the preview (concurrent catalog/tax change).
  ADJUST_SETTLE_PREVIEW_STALE = 'ADJUST_SETTLE_PREVIEW_STALE',

  // Per-payment reseller-commission settle: the accrual is not in a
  // settleable state (already in a payout / paid / voided / non-positive).
  RESELLER_ACCRUAL_NOT_SETTLEABLE = 'RESELLER_ACCRUAL_NOT_SETTLEABLE',

  // Per-surface Pre-Factura control (disable Pre-Factura, force fiscal B/E).
  //   * DISABLED_FOR_SURFACE — invoice-create rejected: a final
  //     invoiceType='invoice' arrived from a surface where Pre-Factura is off
  //     (pending-finalize drafts are allowed through).
  //   * NO_FISCAL_SEQUENCE — issuance blocked at cobrar: surface is
  //     forced-fiscal but the business has no registered B/E sequence.
  //   * REQUIRES_FISCAL_SEQUENCE — settings update rejected: would disable
  //     Pre-Factura on a surface while no B/E sequence is registered.
  PRE_FACTURA_DISABLED_FOR_SURFACE = 'PRE_FACTURA_DISABLED_FOR_SURFACE',
  PRE_FACTURA_NO_FISCAL_SEQUENCE = 'PRE_FACTURA_NO_FISCAL_SEQUENCE',
  PRE_FACTURA_REQUIRES_FISCAL_SEQUENCE = 'PRE_FACTURA_REQUIRES_FISCAL_SEQUENCE',

  // Per-branch access set (users.access_all_branches + user_branch_role).
  //   * BRANCH_ACCESS_DENIED — the request carried an X-Branch-Office-Id
  //     the caller holds no grant for. Thrown by PermissionGuard via
  //     BranchAccessService; the client clears the stale selection and
  //     falls back to its first reachable branch.
  //   * USER_BRANCH_ACCESS_REQUIRED — a user was saved with multi-branch
  //     access enabled and zero branches named, which would leave them
  //     enabled with an empty, unusable access set.
  //   * ALL_BRANCHES_ACCESS_DENIED — the business-wide "all branches"
  //     view was requested by a user who cannot reach every branch.
  //   * USER_BRANCH_ACCESS_INVALID — a grant names a branch or role that
  //     is not this business's.
  //   * USER_SELF_BRANCH_ACCESS_FORBIDDEN — a non-admin tried to change
  //     their own branch access.
  BRANCH_ACCESS_DENIED = 'BRANCH_ACCESS_DENIED',
  USER_BRANCH_ACCESS_REQUIRED = 'USER_BRANCH_ACCESS_REQUIRED',
  ALL_BRANCHES_ACCESS_DENIED = 'ALL_BRANCHES_ACCESS_DENIED',
  USER_BRANCH_ACCESS_INVALID = 'USER_BRANCH_ACCESS_INVALID',
  USER_SELF_BRANCH_ACCESS_FORBIDDEN = 'USER_SELF_BRANCH_ACCESS_FORBIDDEN',

  // Platform-fiscal comprobantes — the platform business (`…001`) issuing
  // DGII comprobantes to tenants for subscription payments.
  //   * PLATFORM_FISCAL_CREDIT_BLOCKED — a tenant's assigned comprobante
  //     requires an RNC (crédito fiscal / gubernamental / regímenes /
  //     exportaciones …) but the tenant has none on file. No comprobante
  //     is issued; a sadmin banner names the tenant.
  //   * PLATFORM_FISCAL_NO_SEQUENCE — the platform has no available NCF
  //     sequence for the assigned comprobante type (or the type is not
  //     issuable). No comprobante is issued; sadmin must register more.
  //   * PLATFORM_FISCAL_CREDIT_NOTE_NO_SEQUENCE — a refund reversal could
  //     not be issued because the platform has no B04/E34 sequence.
  //   * PLATFORM_FISCAL_NO_TAX_DEFINITION — an ITBIS-bearing comprobante was
  //     requested but the platform business has no ITBIS_18 `tax` row (it
  //     has not completed fiscal onboarding). Blocked instead of filing a
  //     0-ITBIS comprobante with DGII; a sadmin banner names the tenant.
  PLATFORM_FISCAL_CREDIT_BLOCKED = 'PLATFORM_FISCAL_CREDIT_BLOCKED',
  PLATFORM_FISCAL_NO_SEQUENCE = 'PLATFORM_FISCAL_NO_SEQUENCE',
  PLATFORM_FISCAL_CREDIT_NOTE_NO_SEQUENCE = 'PLATFORM_FISCAL_CREDIT_NOTE_NO_SEQUENCE',
  PLATFORM_FISCAL_NO_TAX_DEFINITION = 'PLATFORM_FISCAL_NO_TAX_DEFINITION',
}
